/**
 * Snel-kenteken - site-wide behaviour: mega menu, search panel, header shadow.
 * Ported from the Mowi Jewellery H&M-style build (own code). No dependencies.
 * More behaviours (carousels, add-to-bag) land as those features are built.
 */
( () => {
	'use strict';

	const desktop = window.matchMedia( '(min-width: 1024px)' );
	const $ = ( sel, ctx = document ) => ctx.querySelector( sel );
	const $$ = ( sel, ctx = document ) => Array.from( ctx.querySelectorAll( sel ) );

	/* ------------------------------------------------------------------
	 * Overlay + scroll lock shared by the drawer panels
	 * ---------------------------------------------------------------- */
	const overlay = $( '[data-overlay]' );
	let overlayOwner = null;

	function showOverlay( owner ) {
		overlayOwner = owner;
		const header = document.getElementById( 'site-header' );
		if ( header ) {
			document.documentElement.style.setProperty( '--header-bottom', `${ Math.max( 0, Math.round( header.getBoundingClientRect().bottom ) ) }px` );
		}
		if ( overlay ) overlay.hidden = false;
		document.documentElement.classList.add( 'has-overlay' );
	}

	function hideOverlay( owner ) {
		if ( owner && owner !== overlayOwner ) return;
		overlayOwner = null;
		if ( overlay ) overlay.hidden = true;
		document.documentElement.classList.remove( 'has-overlay' );
	}

	/* ------------------------------------------------------------------
	 * Mega menu
	 * ---------------------------------------------------------------- */
	const menu = $( '[data-mega-menu]' );
	const menuToggle = $( '[data-menu-toggle]' );
	let menuTimer = null;
	let activeDept = null;

	function setDept( index ) {
		if ( ! menu ) return;
		activeDept = String( index );
		$$( '[data-dept-panel]', menu ).forEach( ( panel ) => {
			const on = panel.dataset.deptPanel === activeDept;
			panel.hidden = ! on;
			if ( ! on ) closeSub( panel );
		} );
		$$( '[data-dept-tab]', menu ).forEach( ( tab ) => tab.setAttribute( 'aria-selected', String( tab.dataset.deptTab === activeDept ) ) );
		$$( '[data-dept]' ).forEach( ( link ) => link.closest( '.dept-nav__item' ).classList.toggle( 'is-open', link.dataset.dept === activeDept ) );
		$$( '[data-dept-toggle]' ).forEach( ( btn ) => btn.setAttribute( 'aria-expanded', String( btn.dataset.deptToggle === activeDept && ! menu.hidden ) ) );
	}

	function openMenu( index ) {
		if ( ! menu ) return;
		closeSearch();
		menu.hidden = false;
		document.body.classList.add( 'menu-open' );
		setDept( index ?? activeDept ?? currentDeptIndex() );
		if ( menuToggle ) menuToggle.setAttribute( 'aria-expanded', 'true' );
		showOverlay( 'menu' );
	}

	function closeMenu() {
		if ( ! menu || menu.hidden ) return;
		menu.hidden = true;
		document.body.classList.remove( 'menu-open' );
		$$( '.dept-nav__item.is-open' ).forEach( ( li ) => li.classList.remove( 'is-open' ) );
		$$( '[data-dept-toggle]' ).forEach( ( btn ) => btn.setAttribute( 'aria-expanded', 'false' ) );
		if ( menuToggle ) menuToggle.setAttribute( 'aria-expanded', 'false' );
		hideOverlay( 'menu' );
	}

	function currentDeptIndex() {
		const current = $( '.dept-nav__item.is-current [data-dept]' );
		return current ? current.dataset.dept : ( $( '[data-dept-panel]' )?.dataset.deptPanel ?? '0' );
	}

	function openSub( panel, id ) {
		const root = $( '[data-view="root"]', panel );
		const sub = $( `[data-view="${ CSS.escape( id ) }"]`, panel );
		if ( ! root || ! sub ) return;
		root.hidden = true;
		sub.hidden = false;
		$( '[data-close-sub]', sub )?.focus();
	}

	function closeSub( panel ) {
		const root = $( '[data-view="root"]', panel );
		$$( '[data-view]:not([data-view="root"])', panel ).forEach( ( v ) => ( v.hidden = true ) );
		if ( root ) root.hidden = false;
	}

	if ( menu ) {
		$$( '[data-dept]' ).forEach( ( link ) => {
			link.addEventListener( 'mouseenter', () => {
				if ( ! desktop.matches ) return;
				clearTimeout( menuTimer );
				menuTimer = setTimeout( () => openMenu( link.dataset.dept ), menu.hidden ? 120 : 0 );
			} );
			link.addEventListener( 'mouseleave', () => clearTimeout( menuTimer ) );
		} );

		$$( '[data-dept-toggle]' ).forEach( ( btn ) => {
			btn.addEventListener( 'click', () => {
				const same = ! menu.hidden && activeDept === btn.dataset.deptToggle;
				if ( same ) {
					closeMenu();
				} else {
					openMenu( btn.dataset.deptToggle );
					$( `[data-dept-panel="${ btn.dataset.deptToggle }"] .mega-menu__link`, menu )?.focus();
				}
			} );
		} );

		const header = $( '#site-header' );
		header?.addEventListener( 'mouseleave', ( e ) => {
			if ( desktop.matches && ! header.contains( e.relatedTarget ) ) {
				clearTimeout( menuTimer );
				menuTimer = setTimeout( closeMenu, 200 );
			}
		} );
		header?.addEventListener( 'mouseenter', () => clearTimeout( menuTimer ) );

		menuToggle?.addEventListener( 'click', () => {
			if ( ! menu.hidden ) {
				closeMenu();
				return;
			}
			openMenu();
			$( '[data-menu-close]', menu )?.focus( { preventScroll: true } );
		} );

		menu.addEventListener( 'click', ( e ) => {
			if ( e.target.closest( '[data-menu-close]' ) ) {
				closeMenu();
				menuToggle?.focus( { preventScroll: true } );
				return;
			}
			const tab = e.target.closest( '[data-dept-tab]' );
			if ( tab ) {
				setDept( tab.dataset.deptTab );
				return;
			}
			const opener = e.target.closest( '[data-open-sub]' );
			if ( opener ) {
				openSub( opener.closest( '[data-dept-panel]' ), opener.dataset.openSub );
				return;
			}
			if ( e.target.closest( '[data-close-sub]' ) ) {
				const panel = e.target.closest( '[data-dept-panel]' );
				const id = e.target.closest( '[data-view]' ).dataset.view;
				closeSub( panel );
				$( `[data-open-sub="${ CSS.escape( id ) }"]`, panel )?.focus();
			}
		} );

		desktop.addEventListener( 'change', closeMenu );
	}

	/* ------------------------------------------------------------------
	 * Search panel
	 * ---------------------------------------------------------------- */
	const search = $( '#search-panel' );

	function openSearch() {
		if ( ! search ) return;
		closeMenu();
		search.hidden = false;
		$$( '[data-search-toggle]' ).forEach( ( b ) => b.setAttribute( 'aria-expanded', 'true' ) );
		showOverlay( 'search' );
		requestAnimationFrame( () => $( 'input[type="search"]', search )?.focus() );
	}

	function closeSearch() {
		if ( ! search || search.hidden ) return;
		search.hidden = true;
		$$( '[data-search-toggle]' ).forEach( ( b ) => b.setAttribute( 'aria-expanded', 'false' ) );
		hideOverlay( 'search' );
	}

	/* Predictive search: debounced fetch of the rendered "predictive-search"
	   section, injected straight into the results container. */
	const searchInput = $( 'input[type="search"]', search );
	const searchResults = $( '[data-predictive-search-results]', search );
	let searchTimer = null;
	let searchAbort = null;

	async function runPredictiveSearch( term ) {
		if ( ! searchResults ) return;
		if ( ! term ) {
			searchResults.hidden = true;
			searchResults.innerHTML = '';
			return;
		}
		searchAbort?.abort();
		searchAbort = new AbortController();
		try {
			const url = `/search/suggest?q=${ encodeURIComponent( term ) }&resources[type]=product&resources[limit]=6&section_id=predictive-search`;
			const res = await fetch( url, { signal: searchAbort.signal } );
			if ( ! res.ok ) return;
			const html = await res.text();
			searchResults.innerHTML = html;
			searchResults.hidden = false;
		} catch ( err ) {
			if ( err.name !== 'AbortError' ) searchResults.hidden = true;
		}
	}

	searchInput?.addEventListener( 'input', () => {
		clearTimeout( searchTimer );
		const term = searchInput.value.trim();
		searchTimer = setTimeout( () => runPredictiveSearch( term ), 250 );
	} );

	$$( '[data-search-toggle]' ).forEach( ( btn ) => btn.addEventListener( 'click', () => ( search.hidden ? openSearch() : closeSearch() ) ) );

	overlay?.addEventListener( 'click', () => {
		closeMenu();
		closeSearch();
	} );

	document.addEventListener( 'keydown', ( e ) => {
		if ( 'Escape' !== e.key ) return;
		if ( menu && ! menu.hidden ) {
			closeMenu();
			( menuToggle && ! desktop.matches ? menuToggle : $( `[data-dept-toggle="${ activeDept }"]` ) )?.focus();
		}
		closeSearch();
	} );

	/* ------------------------------------------------------------------
	 * Header shadow once the page scrolls
	 * ---------------------------------------------------------------- */
	const headerEl = $( '#site-header' );
	if ( headerEl ) {
		const sentinel = document.createElement( 'div' );
		sentinel.className = 'header-sentinel';
		// The sticky element is the section wrapper, so the sentinel has to sit
		// outside it or it would scroll along and never leave the viewport.
		( headerEl.closest( '.shopify-section' ) || headerEl ).before( sentinel );
		new IntersectionObserver( ( [ entry ] ) => headerEl.classList.toggle( 'is-stuck', ! entry.isIntersecting ) ).observe( sentinel );
	}

	/* ------------------------------------------------------------------
	 * Footer columns: accordions on phones that start closed, always-open
	 * columns on desktop. The markup ships them open so the links still
	 * show without JS.
	 * ---------------------------------------------------------------- */
	const footerCols = $$( 'details.footer-col' );
	if ( footerCols.length ) {
		const syncFooter = () => footerCols.forEach( ( col ) => ( col.open = desktop.matches ) );
		syncFooter();
		desktop.addEventListener( 'change', syncFooter );
		footerCols.forEach( ( col ) => $( 'summary', col )?.addEventListener( 'click', ( e ) => {
			if ( desktop.matches ) e.preventDefault();
		} ) );
	}

	// Public helpers for shop.js (add-to-bag, filters, carousels - added later).
	window.snel = { showOverlay, hideOverlay, closeMenu, closeSearch };
} )();
